import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
    getExpenseSettings,
    updateExpenseSettings,
    getExpenseTypes
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Settings, Car, Utensils, Save, RefreshCw } from 'lucide-react';

export default function ExpenseSettingsPage() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expenseTypes, setExpenseTypes] = useState(null);
    const [settings, setSettings] = useState({
        mileage_rate_own_car: 25.0,
        mileage_rate_company_car: 12.0,
        mileage_rate_motorcycle: 9.0,
        mileage_rate_electric: 9.50,
        allowance_domestic_day: 260.0,
        allowance_domestic_night: 140.0,
        allowance_international_day: 520.0,
        meal_deduction_breakfast: 52.0,
        meal_deduction_lunch: 78.0,
        meal_deduction_dinner: 78.0,
        allowance_intl_rate_url: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [settingsRes, typesRes] = await Promise.all([
                getExpenseSettings(),
                getExpenseTypes()
            ]);
            if (settingsRes.data) {
                setSettings(prev => ({ ...prev, ...settingsRes.data }));
            }
            setExpenseTypes(typesRes.data);
        } catch (error) {
            toast.error(t('expense_settings.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateExpenseSettings(settings);
            toast.success(t('expense_settings.save_success'));
        } catch (error) {
            toast.error(error.response?.data?.detail || t('expense_settings.failed_save'));
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = () => {
        // Reset only the numeric rates — preserve the admin-maintained
        // Skatteverket link, which is unrelated to the per-diem amounts.
        setSettings(prev => ({
            ...prev,
            mileage_rate_own_car: 25.0,
            mileage_rate_company_car: 12.0,
            mileage_rate_motorcycle: 9.0,
            mileage_rate_electric: 9.50,
            allowance_domestic_day: 260.0,
            allowance_domestic_night: 140.0,
            allowance_international_day: 520.0,
            meal_deduction_breakfast: 52.0,
            meal_deduction_lunch: 78.0,
            meal_deduction_dinner: 78.0
        }));
        toast.info(t('expense_settings.reset_success'));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="expense-settings-page">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <div>
                    <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
                        <Settings className="w-6 h-6 text-primary" />
                        {t('expense_settings.title')}
                    </h1>
                    <p className="text-muted-foreground mt-1">{t('expense_settings.subtitle')}</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={resetToDefaults}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('expense_settings.reset_defaults')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving}>
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('common.saving') : t('expense_settings.save_settings')}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Mileage Rates */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Car className="w-5 h-5 text-blue-600" />
                            {t('expense_settings.mileage_title')}
                        </CardTitle>
                        <CardDescription>
                            {t('expense_settings.mileage_description')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.own_car')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={settings.mileage_rate_own_car}
                                        onChange={(e) => setSettings({ ...settings, mileage_rate_own_car: parseFloat(e.target.value) || 0 })}
                                        className="pr-14"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr/mil</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_own_car')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.company_car')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={settings.mileage_rate_company_car}
                                        onChange={(e) => setSettings({ ...settings, mileage_rate_company_car: parseFloat(e.target.value) || 0 })}
                                        className="pr-14"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr/mil</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_company_car')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.motorcycle')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={settings.mileage_rate_motorcycle}
                                        onChange={(e) => setSettings({ ...settings, mileage_rate_motorcycle: parseFloat(e.target.value) || 0 })}
                                        className="pr-14"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr/mil</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_motorcycle')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.electric_car')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="0.5"
                                        value={settings.mileage_rate_electric}
                                        onChange={(e) => setSettings({ ...settings, mileage_rate_electric: parseFloat(e.target.value) || 0 })}
                                        className="pr-14"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr/mil</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_electric')}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Allowance Rates */}
                <Card>
                    <CardHeader>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Utensils className="w-5 h-5 text-amber-600" />
                            {t('expense_settings.allowance_title')}
                        </CardTitle>
                        <CardDescription>
                            {t('expense_settings.allowance_description')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.domestic_day')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.allowance_domestic_day}
                                        onChange={(e) => setSettings({ ...settings, allowance_domestic_day: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_domestic_day')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.domestic_night')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.allowance_domestic_night}
                                        onChange={(e) => setSettings({ ...settings, allowance_domestic_night: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_domestic_night')}</p>
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.international_day')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.allowance_international_day}
                                        onChange={(e) => setSettings({ ...settings, allowance_international_day: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.tax_free_international_day')}</p>
                            </div>
                            <div className="col-span-2">
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.intl_rate_url_label')}</Label>
                                <Input
                                    type="url"
                                    inputMode="url"
                                    value={settings.allowance_intl_rate_url || ''}
                                    onChange={(e) => setSettings({ ...settings, allowance_intl_rate_url: e.target.value })}
                                    placeholder="https://www.skatteverket.se/..."
                                />
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.intl_rate_url_hint')}</p>
                            </div>
                        </div>
                        <a
                            href="https://www.skatteverket.se/foretag/arbetsgivare/lonochersattning/traktamenteochbilersattning.4.361dc8c15312eff6fd1703e.html"
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-muted-foreground underline hover:text-foreground"
                        >
                            {t('expense_settings.verify_rates_link')}
                        </a>
                    </CardContent>
                </Card>

                {/* Meal Deductions */}
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg flex items-center gap-2">
                            <Utensils className="w-5 h-5 text-red-600" />
                            {t('expense_settings.meal_title')}
                        </CardTitle>
                        <CardDescription>
                            {t('expense_settings.meal_description')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-6">
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.breakfast')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.meal_deduction_breakfast}
                                        onChange={(e) => setSettings({ ...settings, meal_deduction_breakfast: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.breakfast_standard')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.lunch')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.meal_deduction_lunch}
                                        onChange={(e) => setSettings({ ...settings, meal_deduction_lunch: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.lunch_standard')}</p>
                            </div>
                            <div>
                                <Label className="text-xs uppercase mb-2 block text-muted-foreground">{t('expense_settings.dinner')}</Label>
                                <div className="relative">
                                    <Input
                                        type="number"
                                        step="1"
                                        value={settings.meal_deduction_dinner}
                                        onChange={(e) => setSettings({ ...settings, meal_deduction_dinner: parseFloat(e.target.value) || 0 })}
                                        className="pr-8"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">kr</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('expense_settings.dinner_standard')}</p>
                            </div>
                        </div>

                        <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                            <p className="text-sm text-muted-foreground">
                                <strong>{t('expense_settings.note')}</strong> {t('expense_settings.meal_note_text')}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info Card */}
            <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="pt-6">
                    <h3 className="font-medium mb-2">{t('expense_settings.info_title')}</h3>
                    <div className="text-sm text-muted-foreground space-y-2">
                        <p>
                            • <strong>{t('expense_settings.info_mileage_label')}</strong> {t('expense_settings.info_mileage')}
                        </p>
                        <p>
                            • <strong>{t('expense_settings.info_allowance_label')}</strong> {t('expense_settings.info_allowance')}
                        </p>
                        <p>
                            • <strong>{t('expense_settings.info_night_label')}</strong> {t('expense_settings.info_night')}
                        </p>
                        <p>
                            • <strong>{t('expense_settings.info_meals_label')}</strong> {t('expense_settings.info_meals')}
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
